import classnames from 'classnames';
import React, { useState } from 'react';
import { Dropdown, DropdownButton, Table } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import {
  DjangoListResponse,
  TeamMutationObject,
  UserStatusActionType,
  ReadOnlyUser,
} from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import {
  getUserStatus,
  getUserStatusChangeAction,
  USER_STATUSES,
  USER_STATUSES_ACTIONS,
} from 'v2/utils/user';
import styles from './myTeam.module.scss';
import activeMembersListStyles from './activeMembersList.module.scss';
import { infiniteStrapiUsersKeys } from 'v2/queries';

interface ActiveMembersListProps {
  list: DjangoListResponse<ReadOnlyUser> | undefined;
  currentUserId: number;
}

const ActiveMembersList = ({ list, currentUserId }: ActiveMembersListProps) => {
  const queryClient = useQueryClient();
  const [memberToDisable, setMemberToDisable] = useState<ReadOnlyUser | null>(
    null
  );

  const mutation = useMutation(
    (data: TeamMutationObject) =>
      restFactory.patch<ReadOnlyUser>(`/data-providers/team/${data.id}/`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(infiniteStrapiUsersKeys.all);
        toast.success('Team member updated.');
        setMemberToDisable(null);
      },
      onError: (error: Error) => {
        toast.error(String(error));
      },
    }
  );

  const onChangeStatus = (
    user: ReadOnlyUser,
    newStatus: UserStatusActionType
  ) => {
    const currentStatus = getUserStatusChangeAction(user);
    if (currentStatus === newStatus) {
      return;
    }
    switch (newStatus) {
      case 'Admin':
        mutation.mutate({ id: user.id, admin: true, active: true });
        break;
      case 'Disable':
        setMemberToDisable(user);
        break;
      case 'Member':
        mutation.mutate({ id: user.id, admin: false, active: true });
        break;
    }
  };

  return (
    <>
      <Table>
        <thead>
          <tr>
            <th className={classnames('text-light4', styles.tableHeader)}>
              Name
            </th>
            <th className={classnames('text-light4', styles.tableHeader)}>
              Email address
            </th>
            <th className={classnames('text-light4', styles.tableHeader)}>
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {list?.results.map((item) => {
            const userStatusTitle = getUserStatus(item);
            const isDisabled = userStatusTitle === 'Disabled';
            const disabledStyle = isDisabled ? 'opacity-05' : '';
            return (
              <tr key={item.id}>
                <td
                  className={classnames(
                    'py-4 align-middle',
                    disabledStyle,
                    styles.boldText
                  )}
                >
                  {item.first_name} {item.last_name}
                </td>
                <td
                  className={classnames(
                    'py-4 align-middle',
                    disabledStyle,
                    styles.boldText
                  )}
                >
                  {item.email}
                </td>
                <td className="py-4 align-middle">
                  {item.id === currentUserId ? null : (
                    <DropdownButton
                      className={activeMembersListStyles.dropDownBtn}
                      variant="white"
                      title={
                        <span
                          className={classnames('pr-2', {
                            'text-danger': userStatusTitle === 'Disabled',
                          })}
                        >
                          {userStatusTitle}
                        </span>
                      }
                    >
                      {USER_STATUSES_ACTIONS.map((state, index) => (
                        <React.Fragment key={state}>
                          <Dropdown.Item
                            as="button"
                            className={classnames({
                              'text-danger': state === 'Disable',
                            })}
                            onClick={() => onChangeStatus(item, state)}
                          >
                            {state}
                          </Dropdown.Item>
                          {index < USER_STATUSES.length - 1 ? (
                            <Dropdown.Divider />
                          ) : null}
                        </React.Fragment>
                      ))}
                    </DropdownButton>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <ConfirmationModal
        show={!!memberToDisable}
        onCancel={() => setMemberToDisable(null)}
        onOk={async () => {
          if (memberToDisable) {
            mutation.mutate({ id: memberToDisable.id, active: false });
          }
        }}
        okBtnText="Yes"
        cancelBtnText="No"
        title="Disable team member"
        isLoading={mutation.isLoading}
      >
        <>
          <strong>
            {memberToDisable?.first_name} {memberToDisable?.last_name || ''}{' '}
          </strong>
          will now be disabled. They will no longer be able to log in, respond
          to chats or receive any email notifications. Even if enabled later,
          their old conversations will remain closed.
          <br />
          <br />
          Are you sure?
        </>
      </ConfirmationModal>
    </>
  );
};

export default ActiveMembersList;
